import AuthenticationContext from 'adal-angular';

import { isAdalThirdPartyCookieError, requiresInteractiveLogin } from '../adal';
import { AcquireTokenAuthError, LoginInProgressAuthError } from '../errors';
import type { Authenticator, AuthenticatorOptions, ThirdPartyCookieErrorCallback } from '../types';
import { AuthenticatorType } from '../types';

const loadFrameTimeout = 6000;
const oldTenantKey = 'yammer.login.tenant.old';

const rejectAfter = (ms: number, error: Error) =>
  new Promise<never>((_resolve, reject) =>
    setTimeout(() => {
      reject(error);
    }, ms)
  );

/**
  Use MsalRedirectAuthenticator instead.
  ADAL is no longer supported by Microsoft and no further security updates will be released.
  @deprecated 
*/
export default class AdalRedirectAuthenticator implements Authenticator {
  public readonly type = AuthenticatorType.AdalRedirect;
  private readonly context: AuthenticationContext;
  private readonly onError: AuthenticatorOptions['onError'];
  private readonly currentTenant: string;
  private onThirdPartyCookieError?: ThirdPartyCookieErrorCallback;

  constructor(options: AuthenticatorOptions) {
    this.currentTenant = options.tenant;
    this.onError = options.onError;
    this.context = new AuthenticationContext({
      instance: options.instance,
      tenant: options.tenant,
      clientId: options.clientId,
      redirectUri: options.redirectUri,
      extraQueryParameter: options.extraQueryParameters,
      cacheLocation: 'localStorage',
      navigateToLoginRequestUrl: true,
      loadFrameTimeout,
      postLogoutRedirectUri: options.postLogoutRedirectUri,
    });
  }

  public async acquireToken([resource]: string[]): Promise<string> {
    if (this.requiresLogin()) {
      this.login();
      throw new LoginInProgressAuthError('Not yet authenticated. Redirecting to interactive login flow.');
    }
    this.clearCacheIfRequired();
    try {
      this.handleTokenInUrl();

      return await this.acquireTokenSilent(resource);
    } catch (firstError) {
      await this.reportError(resource, new AcquireTokenAuthError({ ...firstError, attempts: 1 }));

      if (isAdalThirdPartyCookieError(firstError) && this.onThirdPartyCookieError) {
        this.onThirdPartyCookieError(resource);
        throw firstError;
      }

      if (requiresInteractiveLogin(firstError)) {
        this.acquireTokenRedirect([resource]);
        throw firstError;
      }

      try {
        return await this.acquireTokenSilent(resource);
      } catch (secondError) {
        await this.reportError(resource, new AcquireTokenAuthError({ ...secondError, attempts: 2 }));

        if (isAdalThirdPartyCookieError(secondError) && this.onThirdPartyCookieError) {
          this.onThirdPartyCookieError(resource);
          throw secondError;
        }

        if (requiresInteractiveLogin(secondError)) {
          this.acquireTokenRedirect([resource]);
        }
        throw secondError;
      }
    }
  }

  public clearCacheIfRequired(): void {
    const oldTenant = window.localStorage.getItem(oldTenantKey);
    if (this.currentTenant !== oldTenant) {
      window.localStorage.setItem(oldTenantKey, this.currentTenant);

      if (!oldTenant) {
        return;
      }

      this.context.clearCache();
    }
  }

  public acquireTokenRedirect([resource]: string[]): void {
    this.context.acquireTokenRedirect(resource);
  }

  public setThirdPartyCookieErrorCallback(onThirdPartyCookieError: ThirdPartyCookieErrorCallback) {
    this.onThirdPartyCookieError = onThirdPartyCookieError;
  }

  public handleTokenInUrl() {
    this.context.handleWindowCallback(window.location.hash);
  }

  public logout() {
    this.context.logOut();
  }

  private requiresLogin() {
    return !this.getUser() && !this.hasTokenInUrl();
  }

  private login() {
    this.context.login();
  }

  private getUser() {
    return this.context.getCachedUser();
  }

  private hasTokenInUrl() {
    return this.context.isCallback(window.location.hash);
  }

  private acquireTokenSilent(resource: string): Promise<string> {
    const acquireTokenPromise = new Promise<string>((resolve, reject) => {
      this.context.acquireToken(resource, (errorMessage, token, errorCode) => {
        if (errorCode) {
          reject(new AcquireTokenAuthError({ errorCode, errorMessage: errorMessage ?? undefined }));

          return;
        }
        resolve(token);
      });
    });
    const timeoutPromise = rejectAfter(loadFrameTimeout * 2, new AcquireTokenAuthError({ errorCode: 'adal_stuck' }));

    return Promise.race([acquireTokenPromise, timeoutPromise]);
  }

  private async reportError(resource: string, error: AcquireTokenAuthError) {
    // istanbul ignore else
    if (this.onError) {
      try {
        await this.onError(error, { authenticatorType: this.type, scopes: [resource] });
      } catch (err) {
        console.error(err);
      }
    }
  }
}
